/**
 * Dates, numbers and money, bound to the active locale.
 *
 * # What this replaces
 *
 * Formatting in the console was locale-blind in three separate ways before this
 * existed, and all three were invisible because the console was English:
 *
 *   - `date-fns` is a dependency of 8 packages and was called with no `locale`
 *     option ANYWHERE, so every date silently rendered en-US. `format(d, 'PP')`
 *     gives "Aug 17, 2026" and no British reader ever writes that.
 *   - 82 `toLocale*String()` calls passed no locale, so they followed whatever
 *     the browser was set to and disagreed with the date-fns calls next to them.
 *   - `Intl.NumberFormat('en-GB')` was hardcoded in 5 places.
 *
 * A translated sentence carrying an en-US date is worse than an untranslated
 * one, because it looks finished.
 *
 * # Currency is not a locale
 *
 * Foir bills in GBP and only in GBP. The locale decides how £1,234.56 is
 * PUNCTUATED (`1.234,56 £` in German), never which currency it is. `currency()`
 * therefore takes no currency argument: there is one, it is GBP, and a call
 * site that could pass the wrong one is a call site that eventually will.
 */

import type { Locale as DateFnsLocale } from 'date-fns';
import { de, enGB, es, fr } from 'date-fns/locale';

/** Foir bills in GBP. See the header. */
const CURRENCY = 'GBP';

/**
 * date-fns locale objects, by catalogue tag.
 *
 * Statically imported rather than dynamically resolved: `date-fns/locale`
 * re-exports every locale on earth, and an unresolvable dynamic import defeats
 * tree-shaking and pulls all of them into a bundle that already needs
 * `--max-old-space-size=4096` to build. One line per catalogue we ship is a
 * chore worth paying.
 *
 * `en` maps to `enGB`, not `enUS`. Foir is a British product and the previous
 * behaviour, falling to date-fns's en-US default, was the bug rather than the
 * spec.
 */
const DATE_LOCALES: Record<string, DateFnsLocale> = {
  en: enGB,
  de,
  es,
  fr,
};

/** The date-fns locale for a tag, falling back the way {@link resolveLocale} does. */
export function dateLocaleFor(locale: string): DateFnsLocale {
  return DATE_LOCALES[locale] ?? DATE_LOCALES[locale.split('-')[0]] ?? enGB;
}

/**
 * The tag to hand `Intl`, which is not always the tag the app resolved.
 *
 * Bare `en` means en-US to `Intl`: "Aug 13, 2026, 3:05 PM". The date-fns half
 * of this module has always mapped `en` to enGB for the reason above, so
 * leaving this one alone had the two halves of the same formatter set
 * disagreeing — a screen showing `date()` beside a date-fns `format()` printed
 * one American and one British.
 */
export function intlLocaleFor(locale: string): string {
  return locale === 'en' ? 'en-GB' : locale;
}

export interface Formatters {
  /** The locale these are bound to. Useful for `Intl` calls this does not wrap. */
  readonly locale: string;
  /** The date-fns locale object, to pass as `{ locale }` to `format()`. */
  readonly dateLocale: DateFnsLocale;
  /** A date, no time. */
  date(value: Date | number): string;
  /** A date and a time. */
  dateTime(value: Date | number): string;
  /** A time, no date. */
  time(value: Date | number): string;
  /** "3 days ago", "in 2 hours". */
  relative(value: Date | number, now?: Date | number): string;
  /** A plain number, grouped and punctuated for the locale. */
  number(value: number, options?: Intl.NumberFormatOptions): string;
  /**
   * Money. Always GBP. See the header.
   *
   * `options` tunes the PUNCTUATION only — `style` and `currency` are applied
   * after it, so a caller can ask for whole pounds and cannot ask for dollars.
   */
  currency(value: number, options?: Intl.NumberFormatOptions): string;
  /** `0.42` -> "42%". */
  percent(value: number, fractionDigits?: number): string;
  /** Bytes at human scale: "1.2 MB". */
  bytes(value: number): string;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * Build the formatter set for a locale.
 *
 * Every `Intl.*Format` instance is constructed once here rather than per call.
 * Constructing them is the expensive part, and a table rendering 500 cells was
 * previously building 500 of them.
 */
export function createFormatters(appLocale: string): Formatters {
  const locale = intlLocaleFor(appLocale);
  const date = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
  const dateTime = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const time = new Intl.DateTimeFormat(locale, { timeStyle: 'short' });
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const decimal = new Intl.NumberFormat(locale);
  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: CURRENCY,
  });

  return {
    locale,
    dateLocale: dateLocaleFor(appLocale),
    date: (v) => date.format(v),
    dateTime: (v) => dateTime.format(v),
    time: (v) => time.format(v),
    relative: (v, now = Date.now()) => {
      const delta = new Date(v).getTime() - new Date(now).getTime();
      const abs = Math.abs(delta);
      const [unit, ms]: [Intl.RelativeTimeFormatUnit, number] =
        abs < MINUTE ? ['second', SECOND]
        : abs < HOUR ? ['minute', MINUTE]
        : abs < DAY ? ['hour', HOUR]
        : abs < WEEK ? ['day', DAY]
        : abs < MONTH ? ['week', WEEK]
        : abs < YEAR ? ['month', MONTH]
        : ['year', YEAR];
      return relative.format(Math.round(delta / ms), unit);
    },
    number: (v, options) =>
      options ? new Intl.NumberFormat(locale, options).format(v) : decimal.format(v),
    currency: (v, options) =>
      options
        ? new Intl.NumberFormat(locale, {
            ...options,
            style: 'currency',
            currency: CURRENCY,
          }).format(v)
        : money.format(v),
    percent: (v, fractionDigits = 0) =>
      new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(v),
    bytes: (v) => {
      // A missing plan limit arrives as NaN and an empty bucket as 0, and both
      // used to render "NaN undefined" once the scaling ran on them.
      if (!Number.isFinite(v) || v <= 0) return '0 B';
      // `unit: 'byte'` with `notation: 'compact'` is not supported widely enough
      // to rely on, so the scaling is explicit and the locale only punctuates.
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let n = v;
      let i = 0;
      while (n >= 1024 && i < units.length - 1) {
        n /= 1024;
        i++;
      }
      // Max, not min: 1.5 KB keeps its decimal, 5 MB does not grow a "5.0".
      const digits = i === 0 || n >= 100 ? 0 : 1;
      return `${new Intl.NumberFormat(locale, {
        maximumFractionDigits: digits,
      }).format(n)} ${units[i]}`;
    },
  };
}
